import { useState } from "react";
import { CreditCard, Lock, DollarSign } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Payment() {
  const [form, setForm] = useState({
    amount: "",
    customer_name: "",
    customer_email: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    if (!form.customer_name || !form.customer_email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout/session", {
        amount: amt,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        description: form.description,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section
      id="payment"
      data-testid="payment-section"
      className="relative py-24 md:py-32 bg-[#0B1810] text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20 mix-blend-overlay"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/13956349/pexels-photo-13956349.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-4 text-[#FF5A00]">Secure Payment</div>
          <h2 className="h-section mb-6">
            Pay Your
            <br />
            <span className="text-[#FF5A00]">Invoice.</span>
          </h2>
          <p className="text-white/75 font-body text-lg mb-8 leading-relaxed">
            Settle an invoice, pay a deposit, or leave a tip for the crew.
            Payments are processed securely by Stripe — cards, Apple Pay and
            Google Pay all accepted.
          </p>

          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <Lock size={18} className="text-[#FF5A00]" />
              <span className="font-body">256-bit SSL encrypted checkout</span>
            </li>
            <li className="flex items-center gap-3">
              <CreditCard size={18} className="text-[#FF5A00]" />
              <span className="font-body">Visa, Mastercard, Amex, Discover</span>
            </li>
            <li className="flex items-center gap-3">
              <DollarSign size={18} className="text-[#FF5A00]" />
              <span className="font-body">No hidden fees. Receipt emailed.</span>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="payment-form"
            onSubmit={submit}
            className="bg-[#F4F1EA] text-[#1A1A1A] p-8 md:p-10 border-2 border-[#FF5A00] shadow-[10px_10px_0_#FF5A00]"
          >
            <h3 className="font-heading text-3xl md:text-4xl mb-6 text-[#0B1810]">
              Payment Details
            </h3>

            <div className="space-y-5">
              <div>
                <label className="hc-label" htmlFor="pay-amount">
                  Amount (USD)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-2xl text-[#0B1810]">
                    $
                  </span>
                  <input
                    id="pay-amount"
                    data-testid="payment-amount-input"
                    className="hc-input !pl-10 font-heading !text-2xl"
                    type="number"
                    min="1"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) =>
                      setForm({ ...form, amount: e.target.value })
                    }
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="hc-label" htmlFor="pay-name">
                    Full Name
                  </label>
                  <input
                    id="pay-name"
                    data-testid="payment-name-input"
                    className="hc-input"
                    type="text"
                    value={form.customer_name}
                    onChange={(e) =>
                      setForm({ ...form, customer_name: e.target.value })
                    }
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="hc-label" htmlFor="pay-email">
                    Email
                  </label>
                  <input
                    id="pay-email"
                    data-testid="payment-email-input"
                    className="hc-input"
                    type="email"
                    value={form.customer_email}
                    onChange={(e) =>
                      setForm({ ...form, customer_email: e.target.value })
                    }
                    placeholder="you@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="hc-label" htmlFor="pay-desc">
                  Description (optional)
                </label>
                <input
                  id="pay-desc"
                  data-testid="payment-description-input"
                  className="hc-input"
                  type="text"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Invoice #1234 — tree removal deposit"
                />
              </div>

              <button
                type="submit"
                data-testid="payment-submit-button"
                disabled={loading}
                className="btn-primary w-full justify-center disabled:opacity-60"
              >
                <Lock size={18} />
                {loading ? "Redirecting..." : "Continue to Secure Checkout"}
              </button>

              <p className="text-sm text-[#4A4D4A] font-body text-center mt-2">
                You'll be redirected to Stripe to complete your payment.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
